<template>
    <NuxtLayout name="malt-base-layout">
        <VJoyTemplate class="report" full>
            <template #template-title>
                <slot name="title" />
            </template>
            <template #template-subtitle>
                <div class="report__subtitle">{{ projectSubject }}</div>
            </template>
            <template v-if="!isProjectCancelled" #template-actions>
                <slot name="actions" />
            </template>
            <template #template-main>
                <slot name="highlight" />
                <div v-if="!isProjectCancelled">
                    <DetailedReport v-if="isReportTypeDetailed" />
                    <SimpleReport v-if="!isReportTypeDetailed" />
                    <slot name="mobile-actions" />
                </div>
            </template>
        </VJoyTemplate>
    </NuxtLayout>
</template>

<script setup lang="ts">
    import {watch} from 'vue';
    import debounce from 'lodash-es/debounce';
    import {storeToRefs} from 'pinia';
    import {useTranslation, useHead} from '#imports';
    import {useCollectScope} from '@malt/forms';
    import {VJoyTemplate} from '@maltjoy/core-vue';
    import {useActivityTrackerStore, useEarningsOfTheMonthStore} from '@/common/stores';
    import DetailedReport from '@/features/detailed-report/ui/DetailedReport.vue';
    import SimpleReport from '@/features/simple-report/ui/SimpleReport.vue';

    const activityTrackerStore = useActivityTrackerStore();
    const earningsOfTheMonthStore = useEarningsOfTheMonthStore();
    const {simpleReportTotalWorkAmount, projectSubject, isProjectCancelled, isReportTypeDetailed, detailedReportWorkTotalAmount, activityReportId} =
        storeToRefs(activityTrackerStore);
    const {fetchActivityTracker} = activityTrackerStore;
    const {fetchEarnings} = earningsOfTheMonthStore;
    const {t} = useTranslation();
    const {r$} = useCollectScope();

    await fetchActivityTracker();
    await monthlyEarningsFetchHandler();

    const onMonthlyEarningsDebounced = debounce(monthlyEarningsFetchHandler, 1000);

    watch([detailedReportWorkTotalAmount, simpleReportTotalWorkAmount], () => {
        onMonthlyEarningsDebounced();
    });

    useHead({
        title: t('activity-tracker.page.title'),
    });

    async function monthlyEarningsFetchHandler() {
        if (r$.$invalid) {
            return;
        }

        await fetchEarnings(
            activityReportId.value!,
            isReportTypeDetailed.value ? detailedReportWorkTotalAmount.value! : (simpleReportTotalWorkAmount.value ?? 0),
        );
    }
</script>

<style lang="scss" scoped>
    $icon-with: 16px;

    .report {
        min-height: 85vh;

        :deep(.joy-wrapper) {
            margin-bottom: 0;
        }

        :deep(.joy-highlight) {
            padding: var(--joy-core-spacing-4);
        }

        &__subtitle {
            margin-left: calc(var(--joy-core-spacing-4) + #{$icon-with});
        }

        &__save-btn:disabled {
            background-color: var(--joy-color-neutral-20);
        }

        &__btns-wrapper {
            gap: var(--joy-core-spacing-2);
        }

        &__highlight {
            margin-bottom: var(--joy-core-spacing-8);
        }
    }

    .divider {
        margin: var(--joy-core-spacing-6);
        background-color: var(--joy-color-neutral-50);
        border-radius: var(--joy-core-radius-3);
        width: 32px;
        height: 6px;

        &__wrapper {
            display: flex;
            justify-content: center;
            align-items: center;
            border-bottom: 1px solid var(--joy-color-neutral-20);
            margin-bottom: var(--joy-core-spacing-4);
        }
    }
</style>
